import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { publicIp } from 'public-ip';
import { useNetwork } from '../../utils/network';
import { useShowSnackbar } from '../../utils/snackbar';
import { track, trackError, trackFormError } from '../../utils/track';
import { validateNameValue } from '../../utils/nameValidation';
import { emailValidationRegex } from '../../constants/regex';
import { CaptchaModal } from '../../components/CaptchaModal';
import { saveGiveawayOfferDraft } from './giveawayOfferFlowState';
import { useOfferSlug } from './giveawayOfferPages';
import { freeEntriesCopy } from './giveawayOfferEntries';
import {
  PAGE_BG,
  CYAN,
  GOLD,
  INK,
  BODY,
  MUTED,
  kickerSx,
  ctaSx,
  panelSx,
  darkFieldSx,
  fieldLabelSx
} from './giveawayOfferTheme';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import RaffleFooter from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import { GiveawayCountdownTimer } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleHero/GiveawayCountdownTimer';

/* `/giveaway-offer` — the QR / poster funnel.
 *
 * THIS FORM ASKS THE SERVER NOTHING UNTIL SUBMIT, and that is the entire reason
 * it is a new page rather than a revival of `features/PartnerSignup`. That funnel
 * is disabled because its form probed `/auth/check-email` on every keystroke,
 * which told anyone who typed an address whether we already had it — and it did
 * the same with `/auth/check-phone`. There is no `check-*` call anywhere in this
 * file, and adding one would recreate exactly what got the last one switched off.
 *
 * WHAT REMAINS, stated plainly rather than papered over: `POST /auth/sign-up`
 * itself answers with distinct messages for a taken email, username or phone.
 * That is pre-existing and shared with `/sign-up`; what pays for it is that
 * Turnstile runs BEFORE those lookups, so it is not a free bulk oracle. This page
 * inherits that trade-off, it does not widen it.
 *
 * THE ENTRY COUNT IS NOT A CONSTANT HERE, NOR ONE FIGURE FOR EVERY POSTER. Each
 * code carries its own amount, and `GET /giveaway/offer?c=CODE` answers with
 * that code's — the same source the server grants from, so the poster's promise
 * and the grant cannot drift apart. The code goes on to the confirmation page in
 * its URL, so that page asks for the same amount.
 *
 * AND THE ENTRIES ARE NOT GRANTED YET when this form succeeds. They land on email
 * verification (`server/lib/initialGiveawayEntries.js`), which is why the copy
 * says "claim" and the confirmation page says "reserved".
 *
 * NO PHONE, COUNTRY OR STATE HERE (2026-10-07). The admin found the poster
 * sign-up too long, so this form asks for a name, a username and an email and
 * nothing else. The server lets a code-validated QR sign-up leave the three out
 * (`authController.signUp`, `contactLater`), stores them empty, and the member
 * is asked for them the first time they open the portal, in a dialog they cannot
 * dismiss (`MemberPortal/.../ProfileDetailsModal` → `PUT /user/profile-details`).
 * Putting any of the three back here is a product decision, not a bug fix.
 */

type Offer = {
  entries: number;
  giveawayName: string | null;
  endDate: string | null;
  prizeAmount: number | null;
};

type Values = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  referralCode: string;
};

const formatPrize = (amount: number | null) =>
  amount ? `A$${Number(amount).toLocaleString('en-AU')}` : 'our giveaway';



const GiveawayOfferPage = () => {
  const navigate = useNavigate();
  const { get, post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  /* The path this was opened on — `/giveaway-offer`, or one an admin picked
   * such as `/token2049-offer` — so the member stays on it through the funnel.
   * A URL and an analytics tag only: `signupSource` below does not follow it. */
  const offerSlug = useOfferSlug();

  const [offer, setOffer] = useState<Offer | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [eligible, setEligible] = useState<'yes' | 'no'>('yes');

  /* THE POSTER CODE. `?c=` is put there by the QR code on the printed poster.
   *
   * A code is valid inside its own window and refused outside it, so a poster
   * from a finished promotion has to be turned away — refusing it is the entire
   * point of printing a code at all.
   *
   * SEVERAL CAN BE LIVE AT ONCE. This used to say the code "rotates at 03:00 and
   * only one is ever valid", which was true of the old daily rotation; nothing
   * rotates now, promotions can overlap, and no caller may assume a single
   * "current" code.
   *
   * A visit with no code at all is refused for the same reason: a bare
   * /giveaway-offer that always worked would be a permanent way around every
   * window and every cap. */
  const [searchParams] = useSearchParams();
  const offerCode = (searchParams.get('c') ?? '').trim().toUpperCase();
  const [codeState, setCodeState] = useState<'checking' | 'ok' | 'bad'>(
    'checking'
  );
  const [codeReason, setCodeReason] = useState('');

  const [values, setValues] = useState<Values>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    referralCode: ''
  });

  const setField = <K extends keyof Values>(key: K, val: Values[K]) =>
    setValues((v) => ({ ...v, [key]: val }));

  /* Fetch once, with the request INSIDE the effect and an empty dep array.
   *
   * This was a useCallback keyed on [get] feeding a useEffect keyed on that
   * callback, which loops without end: useNetwork returns a useMemo keyed on
   * [accessToken, baseURL, logout], so each time that memo re-runs `get` is a
   * new identity -> the callback is rebuilt -> the effect re-fires -> setOffer
   * re-renders -> round again. It issued hundreds of requests in seconds.
   *
   * RafflePage fetches exactly this way for the same reason, and the CODEMAP
   * entry for DevSection records the same trap: `get` is deliberately excluded
   * from deps, and the eslint-disable exists to allow that — NOT to silence a
   * warning while leaving `get` in, which is what made this loop.
   *
   * `cancelled` stops a late response calling setState after unmount.
   *
   * IT ASKS WITH THE POSTER CODE, because the number is that code's own amount.
   * The code is read once, at mount, and that is enough: it comes from the URL,
   * and nothing in the funnel changes `?c=` under a mounted page. With no code
   * the page shows a refusal, never this number. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await get(
          offerCode
            ? `/giveaway/offer?c=${encodeURIComponent(offerCode)}`
            : '/giveaway/offer',
          true
        );
        if (!cancelled) setOffer(data);
      } catch {
        /* The page still works without it — the headline falls back to generic
           copy rather than printing a number we could not confirm. */
      }
    })();

    track('giveaway_offer.view', { offerSlug });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Is the code on this poster still valid right now?
   *
   * Checked on load so somebody who scanned a dead poster is told straight
   * away, rather than filling in the form and being refused at submit.
   *
   * THIS IS COURTESY, NOT THE GATE. `authController.signUp` re-validates the
   * code server-side and refuses an expired one there; nothing here is trusted.
   *
   * FAILS OPEN on a network error, deliberately. A dropped request is not a
   * verdict, and the server still refuses a dead code at submit — so the cost
   * of letting them through is a later rejection, while the cost of failing
   * closed is turning away a VALID scan because one request did not land.
   *
   * Same empty-dep shape as the effect above and for the same reason: `get`
   * changes identity every time useNetwork's memo re-runs. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!offerCode) {
        if (!cancelled) {
          setCodeReason('missing');
          setCodeState('bad');
        }
        return;
      }

      try {
        const res = await get(
          `/giveaway/offer/validate?c=${encodeURIComponent(offerCode)}`,
          true
        );
        if (cancelled) return;
        setCodeReason(res?.reason ?? 'unknown');
        setCodeState(res?.valid ? 'ok' : 'bad');
      } catch {
        if (!cancelled) setCodeState('ok');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerCode]);

  /* Only render a countdown for a draw that is still ahead of us.
   *
   * GiveawayCountdownTimer has no "closed" state — at zero it sits on
   * 00:00:00:00 forever — so a finished or stale draw would paint a dead
   * all-zeros clock on a page people reach from a printed QR code, which reads
   * as broken rather than as "between draws". Showing nothing is the honest
   * fallback: the offer itself still stands, and the entries are still granted
   * against whichever jackpot draw is Active when the member verifies. */
  const drawDate = useMemo(() => {
    if (!offer?.endDate) return null;
    const d = new Date(offer.endDate);
    if (Number.isNaN(d.getTime())) return null;
    return d.getTime() > Date.now() ? d : null;
  }, [offer?.endDate]);

  const errors = useMemo(() => {
    const firstName = validateNameValue(values.firstName);
    const lastName = validateNameValue(values.lastName);
    return {
      firstName: values.firstName.trim()
        ? typeof firstName === 'string'
          ? firstName
          : ''
        : 'First name is required.',
      lastName: values.lastName.trim()
        ? typeof lastName === 'string'
          ? lastName
          : ''
        : 'Last name is required.',
      username: values.username.trim() ? '' : 'Username is required.',
      email: emailValidationRegex.test(values.email.trim())
        ? ''
        : 'Enter a valid email address.'
    };
  }, [values]);

  const canSubmit = Object.values(errors).every((e) => !e) && !submitting;

  const openCaptcha = () => {
    setAttempted(true);
    const bad = Object.entries(errors)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (bad.length) {
      trackFormError('giveaway_offer', bad, { offerSlug });
      return;
    }
    setCaptchaOpen(true);
  };

  const submit = async (captchaToken: string) => {
    setSubmitting(true);
    try {
      await post('/auth/sign-up', {
        username: values.username.trim(),
        email: values.email.trim().toLowerCase(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        /* No phone, country or state: the server stores them empty for a
         * code-validated QR sign-up and the portal collects them on first visit
         * (see the note at the top). */
        referralCode: values.referralCode.trim() || null,
        inviteCode: null,
        ipAddress: await publicIp(),
        isGiveawayEligible: eligible === 'yes',
        // what makes this account owed the free entries at verification. The
        // same on EVERY offer path: the server keys off this string, not the URL.
        signupSource: 'giveaway-offer',
        /* Which poster this scan came from. The server re-validates it here at
         * submit: a code that has since expired, been switched off or filled up
         * is refused, and the server's message appears in the error snackbar in
         * the catch below. The check on load is courtesy; THIS is what is
         * actually enforced. */
        signupOfferCode: offerCode,
        captchaToken
      });

      track('auth.signup', {
        source: 'giveaway-offer',
        eligible: eligible === 'yes',
        offerSlug
      });

      /* Carry the typed details to the confirmation page, which now offers a
       * subscription checkout that needs them. Saved AFTER the signup succeeded,
       * so a draft never exists for an account that was not created. It pre-fills
       * a form and nothing more — the server identifies the member by email and
       * re-validates every field. See `giveawayOfferFlowState`.
       *
       * Without a phone or an address the draft cannot be a checkout payload on
       * its own: the confirmation page's dialog then asks for the rest, with
       * these pre-filled (`draftEntryPrefill`). */
      saveGiveawayOfferDraft({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        username: values.username.trim(),
        email: values.email.trim().toLowerCase(),
        /* They ticked it to get here — the form will not submit otherwise. */
        agreeToTerms: true,
        eligible
      });

      setCaptchaOpen(false);
      /* The code goes along in the URL, so the confirmation page asks for THIS
       * code's amount — even once a phone tab the browser purged is reloaded,
       * which the sessionStorage draft above does not always survive. Codes are
       * public: they are printed on the posters.
       *
       * `signedUp` is one of the confirmation page's two proofs that this
       * browser came through the sign-up (see its gate). */
      navigate(`/${offerSlug}/confirmation?c=${encodeURIComponent(offerCode)}`, {
        state: { signedUp: true }
      });
    } catch (error: any) {
      trackError('auth.signup', error, { source: 'giveaway-offer', offerSlug });
      showSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to create your account.',
        'error'
      );
      setSubmitting(false);
      setCaptchaOpen(false);
    }
  };

  const entries = offer?.entries ?? null;
  const fieldErr = (key: keyof typeof errors) =>
    attempted && errors[key] ? errors[key] : '';

  /* A dead or missing poster code never reaches the form.
   *
   * Rendered INSTEAD of the page rather than as a banner above it: the form's
   * only possible outcome would be a 400 from the server gate, and a form
   * that cannot succeed is a worse experience than a straight answer. */
  if (codeState !== 'ok') {
    /* FOUR things can be wrong, and they send the reader somewhere different.
     *
     * NOTHING HERE MAY PROMISE THAT ANOTHER POSTER IS UP RIGHT NOW. Codes exist
     * only while an admin is running a promotion, so between promotions there is
     * no current poster to send anybody to. This copy used to say the posters
     * "change every day" and to go scan "the poster that is up today", which was
     * true under the old daily rotation and is now simply false.
     *
     * `not_yet` is the easy one to get wrong: that code IS ours — it is in the
     * database, its promotion has just not started. That happens whenever a run's
     * posters go up at once, or day two is hung early. Telling that person we do
     * not recognise our own code is untrue, and it reads as "this campaign is
     * broken" rather than "come back when it starts". */
    const state =
      codeReason === 'expired' || codeReason === 'revoked'
        ? {
            /* Two causes, one message: the promotion ran out of time, or an
             * admin switched it off. They mean the same thing to whoever is
             * holding the poster, and "out of date" would be wrong for a code
             * that was pulled in the middle of its run. */
            kicker: 'THIS CODE IS NO LONGER ACTIVE',
            heading: 'This code is no longer valid',
            body: 'This code was part of a promotion that has now finished. Keep an eye out for our next one to claim your free entries.'
          }
        : codeReason === 'not_yet'
          ? {
              kicker: 'THIS POSTER IS NOT LIVE YET',
              heading: 'This code has not started',
              body: 'This code is one of ours — its promotion simply has not started yet. Try again once it begins to claim your free entries.'
            }
          : codeReason === 'full'
            ? {
                /* A capped code that has been used up. NOT the same as expired,
                 * and saying "out of date" here would be untrue — the poster is
                 * current, the places are simply gone. */
                kicker: 'THIS OFFER IS FULLY CLAIMED',
                heading: 'Every spot has been taken',
                body: 'This poster was good for a limited number of sign-ups and they have all gone. Keep an eye out for the next one.'
              }
            : {
                /* NO BODY on purpose — the heading says the whole thing.
                 *
                 * The line that used to sit here told people to go and scan the
                 * QR code on one of our posters, which is advice we cannot give:
                 * between promotions there may be no poster up anywhere, and
                 * this is the state somebody lands in when they reach
                 * /giveaway-offer with no code at all. */
                kicker: 'THIS LINK IS NOT VALID',
                heading: 'We do not recognise this link',
                body: null
              };

    return (
      <Box
        sx={{
          background: PAGE_BG,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <SiteHeader />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2.5,
            py: { xs: 8, md: 12 }
          }}
        >
          {codeState === 'checking' ? (
            <CircularProgress sx={{ color: CYAN }} />
          ) : (
            <Box
              sx={{
                ...panelSx,
                p: { xs: 3, md: 4.5 },
                maxWidth: 520,
                textAlign: 'center'
              }}
            >
              <Typography sx={{ ...kickerSx, mb: 1.5 }}>
                {state.kicker}
              </Typography>

              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 31, md: 40 },
                  fontWeight: 900,
                  lineHeight: 1.1,
                  letterSpacing: '-0.01em',
                  color: INK,
                  /* No trailing gap when nothing follows it. */
                  mb: state.body ? 2 : 0
                }}
              >
                {state.heading}
              </Typography>

              {/* Some refusals have nothing more to say and the heading carries
                  the whole message. An empty Typography would still occupy a
                  line, leaving a gap at the foot of the card. */}
              {state.body && (
                <Typography
                  sx={{ fontSize: { xs: 15.5, md: 16.5 }, color: BODY, lineHeight: 1.65 }}
                >
                  {state.body}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <RaffleFooter />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: PAGE_BG,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <SiteHeader />

      {/* ── the pitch on the left, the form on the right ────────────────
          TWO children, TWO columns at lg — keep it that way. A third child in
          a two-column grid auto-places into the next free cell, which is the
          bug that squeezed the campaign composer's preview into the 216px
          palette column. Below lg this stacks, the only sensible phone layout
          for a form this long. */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 4.5, md: 8 },
          pb: { xs: 6, md: 10 },
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0,1fr) minmax(0,520px)'
          },
          gap: { xs: 4, lg: 7 },
          /* Top-aligned, not centred. The form is much taller than the pitch,
             so centring floats the headline at mid-height and it reads as a
             mistake rather than a two-column hero. */
          alignItems: 'start'
        }}
      >
        {/* LEFT: what they get */}
        <Box sx={{ textAlign: { xs: 'center', lg: 'left' }, pt: { lg: 1.5 } }}>
        <Typography
          sx={{ ...kickerSx, fontSize: { xs: 11, md: 12 }, mb: 1.75 }}
        >
          FREE TO ENTER
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 32, md: 52 },
            fontWeight: 900,
            lineHeight: 1.08,
            color: INK,
            mb: 2
          }}
        >
          {entries !== null ? freeEntriesCopy(entries).claim : 'Claim your free entries'}
          <br />
          into {formatPrize(offer?.prizeAmount ?? null)}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 15.5, md: 17.5 },
            color: BODY,
            maxWidth: { xs: 640, lg: 560 },
            mx: { xs: 'auto', lg: 0 },
            lineHeight: 1.65
          }}
        >
          Create your free account below. Verify your email and your entries are
          activated — no purchase, no card.
        </Typography>

        {/* Same treatment as the confirmation page: the slot is always there,
            and it says so honestly when no draw is ahead of us rather than
            vanishing (which is why the timer looked missing — the local active
            giveaway ended 2026-08-14) or painting a dead 00:00:00:00. */}
        <Box sx={{ mt: { xs: 3.5, md: 4.5 } }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.16em',
              color: MUTED,
              mb: 1.25
            }}
          >
            {drawDate ? 'DRAWN IN' : 'NEXT DRAW'}
          </Typography>

          {drawDate ? (
            <GiveawayCountdownTimer nextGiveaway={drawDate} />
          ) : (
            <Typography
              sx={{ fontSize: { xs: 15.5, md: 17 }, fontWeight: 800, color: INK }}
            >
              Announced soon — claim now and you are in the next draw.
            </Typography>
          )}
        </Box>
      </Box>

        {/* RIGHT: the form */}
        <Box sx={{ width: '100%' }}>
        <Box
          sx={{ ...panelSx, p: { xs: 2.5, md: 3.5 } }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography sx={fieldLabelSx}>First name</Typography>
              <TextField
                fullWidth size="small" sx={darkFieldSx} value={values.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                error={!!fieldErr('firstName')} helperText={fieldErr('firstName')}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Last name</Typography>
              <TextField
                fullWidth size="small" sx={darkFieldSx} value={values.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                error={!!fieldErr('lastName')} helperText={fieldErr('lastName')}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography sx={fieldLabelSx}>Username</Typography>
            <TextField
              fullWidth size="small" sx={darkFieldSx} value={values.username}
              onChange={(e) => setField('username', e.target.value)}
              error={!!fieldErr('username')} helperText={fieldErr('username')}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography sx={fieldLabelSx}>Email</Typography>
            <TextField
              fullWidth size="small" type="email" sx={darkFieldSx} value={values.email}
              onChange={(e) => setField('email', e.target.value)}
              error={!!fieldErr('email')} helperText={fieldErr('email')}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography sx={fieldLabelSx}>Referral code (optional)</Typography>
            <TextField
              fullWidth size="small" sx={darkFieldSx} value={values.referralCode}
              onChange={(e) => setField('referralCode', e.target.value)}
            />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Typography sx={fieldLabelSx}>Are you eligible for giveaways?</Typography>
            <RadioGroup
              row value={eligible}
              onChange={(e) => setEligible(e.target.value as 'yes' | 'no')}
            >
              <FormControlLabel
                value="yes"
                control={<Radio size="small" sx={{ color: MUTED, '&.Mui-checked': { color: CYAN } }} />}
                label="Yes"
                sx={{ '& .MuiFormControlLabel-label': { color: BODY, fontSize: 14.5 } }}
              />
              <FormControlLabel
                value="no"
                control={<Radio size="small" sx={{ color: MUTED, '&.Mui-checked': { color: CYAN } }} />}
                label="No"
                sx={{ '& .MuiFormControlLabel-label': { color: BODY, fontSize: 14.5 } }}
              />
            </RadioGroup>
            {eligible === 'no' && (
              /* Honest rather than hidden: the server refuses entries to anyone
                 not eligible, so saying so here avoids a signup that silently
                 does not do the thing the poster promised. */
              <Typography sx={{ fontSize: 13, color: GOLD, mt: 0.5, lineHeight: 1.55 }}>
                You can still create an account and collect Digital Collectibles,
                but free giveaway entries cannot be added.
              </Typography>
            )}
          </Box>

          <Button
            fullWidth
            onClick={openCaptcha}
            disabled={!canSubmit}
            sx={{ ...ctaSx, mt: 3, width: '100%', py: 1.6 }}
          >
            {submitting ? (
              <CircularProgress size={22} sx={{ color: '#04121a' }} />
            ) : entries !== null ? (
              freeEntriesCopy(entries).claimMy
            ) : (
              'Create my free account'
            )}
          </Button>

          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 1.75, textAlign: 'center', lineHeight: 1.6 }}>
            No purchase necessary. You will receive an email to verify your
            account — that is what activates your entries.
          </Typography>
        </Box>
      </Box>

      </Box>

      <CaptchaModal
        open={captchaOpen}
        onClose={() => setCaptchaOpen(false)}
        onSuccess={submit}
        title="Verify to claim your entries"
        description="Complete the verification to create your account"
      />

      <RaffleFooter />
    </Box>
  );
};

export default GiveawayOfferPage;
